import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { getButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { getAccountDashboardUrl } from 'self-service-api/core/utilities/links';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { HighlightedProductsHeaderConfig } from '../../../constants/merchCards/HighlightedProductsHeaderConfig';
import { SCREENS } from '../../../constants/tracking/Screens';
import { MERCH_CARD } from '../../../constants/tracking/TrackingSources';
import HubProductCTA from '../../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { SuiteCTA } from '../../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useAppSelector } from '../../../reducers';
import { isUngatedForInbound25Selector, isUngatedForRemoveMerchCardSteppersSelector } from '../../../selectors/GatingSelectors';
import { isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { hasProductVersionInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { shouldShowTermSelectorForMerchSelector } from '../../../selectors/TermSelectors';
import { useFTS0017orFTS0018ExperimentEffect } from '../../../utils/experiments/fts0017_fts0018';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import { MerchCardLayoutUI } from '../../../v2/components/MerchCardLayoutUI';
import ProductIcon from '../../common/ProductIcon';
import DetailedTermSelector from '../../common/TermSelectors/DetailedTermSelector';
import { HubCardBadge } from '../../common/badges/HubCardBadge';
import { Inbound25CopyChange } from '../../common/i18n/CopyChangeComponents';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import AdditionalUnitsPickerCompactAll from '../../common/quantityPacks/AdditionalUnitsPickerCompactAll';
import ErrorBoundary from '../../error-boundary/ErrorBoundary';
import RequiredOnboardingSubtextContainer from '../../plan-builder/RequiredOnboardingSubtextContainer';
import { MerchCardTitleContainer } from '../MerchCardSharedComponents';
import SuiteTermSelectorContainer from '../SuiteTermSelectorContainer';
import { FTS0017orFTS0018FeaturesContainer } from '../features-display/features-display-containers/FTS0017OrFTS0018FeaturesContainer';
import { StandardFeaturesContainer } from '../features-display/features-display-containers/StandardFeaturesContainer';
import { CrmSuitePriceMerchCard } from '../price-display/CrmSuitePriceMerchCard';
import HighlightedMerchPriceDisplay from '../price-display/HighlightedMerchPriceDisplay';
import { PriceDisplayContainer } from '../price-display/PriceDisplayContainer';
import { useCrmSuiteBundleSkuId } from '../price-display/useCrmSuitePricingInfo';
import { IncludedSeatsDisplayContainerV1 } from '../seats-display/IncludedSeatsDisplayContainerV1';
import { useShowPricePerMonthPerSeat } from '../seats-display/useSeatsDisplay';
import { SubtextDisplayContainer } from '../subtext-display/SubtextDisplayContainer';
import { useSkuAdditionalUnits } from '../useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const HubCard = ({
  productLevel,
  productLine,
  selectedTab,
  columnStart
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isUngatedForInbound25 = useAppSelector(isUngatedForInbound25Selector);
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    quantityPackApiName,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const ownsProductVersion = useAppSelector(state => isAppReady && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const [isLoadingQuantityPacks, setIsLoadingQuantityPacks] = useState(false);
  const {
    title = '',
    subtitle = '',
    productLevelTitle = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const {
    loading: isLoadingFTS0017orFTS0018Experiment,
    variant: isInFTS0017orFTS0018ExperimentVariant
  } = useFTS0017orFTS0018ExperimentEffect();
  const displayTitle = productLevelTitle && isInFTS0017orFTS0018ExperimentVariant && !isLoadingFTS0017orFTS0018Experiment ? productLevelTitle : title;
  const shouldShowTermSelector = useAppSelector(state => {
    if (!isAppReady || !skuId) return false;
    return shouldShowTermSelectorForMerchSelector(state, {
      skuId
    });
  });
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    queryParamTerm,
    trackingSource: MERCH_CARD,
    productLevel,
    isLoadingQuantityPacks
  };
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const isUngatedForRemoveMerchCardSteppers = useAppSelector(isUngatedForRemoveMerchCardSteppersSelector);
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const quantityPackSkuId = isAdditionalUnitSkuIdAQuantityPack ? additionalUnitSkuId : undefined;
  const quantityPackQuantity = isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0;
  const showFTS0017orFTS0018ExperimentFeatures = isInFTS0017orFTS0018ExperimentVariant && !isLoadingFTS0017orFTS0018Experiment && productLevel === STARTER && !!productLine;
  const shouldUseSuiteComponentsFTS0017orFTS0018Experiment = productLevel === STARTER && isInFTS0017orFTS0018ExperimentVariant && !isLoadingFTS0017orFTS0018Experiment;
  const suiteSkuId = useCrmSuiteBundleSkuId({
    productLevel
  });
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeat({
    skuId: suiteSkuId
  });
  const Title = /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    children: [/*#__PURE__*/_jsx(HubCardBadge, {
      productLine: productLine,
      productLevel: productLevel
    }), /*#__PURE__*/_jsxs(MerchCardTitleContainer, {
      children: [isUngatedForInbound25 ? null : /*#__PURE__*/_jsx(ProductIcon, {
        name: productLine,
        tier: productLevel,
        iconStyle: {
          responsive: false,
          blockSize: '24px'
        }
      }), /*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(Inbound25CopyChange, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: displayTitle
          })
        })
      })]
    })]
  });
  const Subheading = /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    children: isSeatsRedesignUngated && ownsProductVersion ? /*#__PURE__*/_jsx(Link, {
      href: getAccountDashboardUrl(),
      "data-selenium-id": `${MERCH_CARD}-${apiName}-viewAccount-button`,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.viewAccountButton.text"
      })
    }) : /*#__PURE__*/_jsx(Inbound25CopyChange, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitle
      })
    })
  });
  const Pricing = (() => {
    if (!skuId) return null;
    if (isUngatedForRemoveMerchCardSteppers) {
      return /*#__PURE__*/_jsx(PriceDisplayContainer, {
        skuId: skuId,
        queryParamTerm: queryParamTerm,
        apiName: apiName
      });
    }
    if (shouldUseSuiteComponentsFTS0017orFTS0018Experiment) {
      return /*#__PURE__*/_jsx(CrmSuitePriceMerchCard, {
        productLevel: productLevel,
        showPricePerMonthPerSeat: showPricePerMonthPerSeat
      });
    }
    return /*#__PURE__*/_jsx(HighlightedMerchPriceDisplay, {
      queryParamTerm: queryParamTerm,
      skuId: skuId,
      quantityPackSkuId: quantityPackSkuId,
      quantityPackQuantity: quantityPackQuantity,
      quantityPackApiName: quantityPackApiName,
      apiName: apiName,
      setIsLoadingQuantityPacks: setIsLoadingQuantityPacks
    });
  })();
  const Seats = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [20],
    widths: [140],
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      children: /*#__PURE__*/_jsx(IncludedSeatsDisplayContainerV1, {
        skuId: skuId
      })
    })
  });
  const Term = shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [41],
    widths: ['100%'],
    children: shouldUseSuiteComponentsFTS0017orFTS0018Experiment ? /*#__PURE__*/_jsx(SuiteTermSelectorContainer, {
      productLevel: productLevel,
      queryParamTerm: queryParamTerm,
      onChange: setQueryParamTerm,
      trackingSource: MERCH_CARD
    }) : /*#__PURE__*/_jsx(DetailedTermSelector, {
      apiName: apiName,
      onChange: setQueryParamTerm,
      activeTerm: queryParamTerm,
      trackingSource: MERCH_CARD
    })
  });
  const Subtext = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [34],
    widths: ['75%'],
    children: isUngatedForRemoveMerchCardSteppers ? /*#__PURE__*/_jsx(SubtextDisplayContainer, {
      additionalUnitSkuId: additionalUnitSkuId,
      queryParamTerm: queryParamTerm,
      productLine: productLine,
      productLevel: productLevel
    }) : /*#__PURE__*/_jsx(AdditionalUnitsPickerCompactAll, Object.assign({}, additionalUnitsPickerProps))
  });
  const CTAs = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: isMerchCardCTAsDataLoading,
    heights: [47],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
        apiName: apiName,
        trackingSource: MERCH_CARD,
        screen: SCREENS[productLine]
      }),
      children: skuId ? /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
        skuId: skuId,
        trackingSource: MERCH_CARD
      }, ctaVariantProps, {
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          justifyContent: "center",
          gap: "200",
          inlineSize: "100%",
          children: purchaseMotions.map((purchaseMotion, index) => {
            return /*#__PURE__*/_jsx("div", {
              children: shouldUseSuiteComponentsFTS0017orFTS0018Experiment && purchaseMotion === BUY_TOUCHLESS ? /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
                purchaseMotion: purchaseMotion,
                productLevel: STARTER,
                trackingSource: MERCH_CARD,
                screen: SCREENS[productLine],
                purchaseMotionLocation: 'MERCH_CARD',
                use: index === 0 ? 'primary' : 'secondary'
              }, ctaVariantProps)) : /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                purchaseMotion: purchaseMotion,
                skuId: skuId,
                productLine: productLine,
                additionalUnitSkuId: additionalUnitSkuId,
                additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
                trackingSource: MERCH_CARD,
                use: index === 0 ? 'primary' : 'secondary',
                purchaseMotionLocation: "MERCH_CARD"
              }, ctaVariantProps))
            }, purchaseMotion);
          })
        })
      })) : null
    })
  });
  const Footer = skuId && /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
      queryParamTerm: queryParamTerm,
      skuIds: [skuId],
      tab: selectedTab,
      trackingSource: MERCH_CARD,
      includesOnboardingCost: false
    })
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-selenium-id": `${seleniumId}-merch-card`,
    "data-test-id": `${seleniumId}-merch-card`,
    columnStart: columnStart,
    Title: Title,
    Subheading: Subheading,
    Pricing: Pricing,
    Seats: Seats,
    Term: Term,
    Subtext: Subtext,
    CTAs: CTAs,
    Features: showFTS0017orFTS0018ExperimentFeatures ? /*#__PURE__*/_jsx(FTS0017orFTS0018FeaturesContainer, {
      productLine: productLine,
      productLevel: productLevel,
      apiName: apiName,
      isCollapsible: isMobile
    }) : /*#__PURE__*/_jsx(StandardFeaturesContainer, {
      productLine: productLine,
      productLevel: productLevel,
      apiName: apiName,
      isCollapsible: isMobile
    }),
    Footer: Footer,
    isUngatedForInbound25: isUngatedForInbound25
  });
};